/**
 * GlassTabIcon - Web Version
 *
 * Simplified web version without Reanimated animations.
 * Uses CSS transitions for basic hover/active effects.
 *
 * @see GlassTabIcon.tsx for native implementation with Reanimated
 */

import React from 'react';
import { View, StyleSheet, ViewStyle } from 'react-native';
import { BlurView } from 'expo-blur';
import { LinearGradient } from 'expo-linear-gradient';
import { hexToRgba } from '../../lib/design-tokens';

interface GlassTabIconProps {
  /** Gradient colors for glass overlay (60% opacity) */
  gradient: readonly [string, string];
  /** Shadow color for glow effect */
  glowColor: string;
  /** Whether this tab is currently selected */
  isActive: boolean;
  /** Icon component (from @expo/vector-icons or custom) */
  icon: React.ReactNode;
}

export const GlassTabIcon: React.FC<GlassTabIconProps> = ({
  gradient,
  glowColor,
  isActive,
  icon,
}) => {
  // Web: Use CSS boxShadow for glow effect (no deprecation warnings)
  const activeStyle: ViewStyle = isActive ? {
    boxShadow: `0px 0px 10px 0px ${hexToRgba(glowColor, 0.6)}`,
    transform: [{ scale: 1.05 }],
  } as ViewStyle : {};

  return (
    <View
      style={[
        styles.container,
        activeStyle,
      ]}
    >
      <View style={styles.glassContainer}>
        {/* Layer 1: Solid dark base */}
        <View style={[StyleSheet.absoluteFill, { backgroundColor: '#111827' }]} />

        {/* Layer 2: BlurView for frosted glass effect */}
        <BlurView intensity={60} tint="dark" style={StyleSheet.absoluteFill} />

        {/* Layer 3: Gradient overlay (only when active) */}
        {isActive && (
          <LinearGradient
            colors={gradient}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={StyleSheet.absoluteFill}
          />
        )}

        {/* Layer 4: Icon content */}
        <View style={styles.iconWrapper}>{icon}</View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    // CSS transition for smooth state changes on web (web-only property)
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  } as ViewStyle,
  glassContainer: {
    width: 48,
    height: 48,
    borderRadius: 12,
    overflow: 'hidden',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.1)',
  } as ViewStyle,
  iconWrapper: {
    zIndex: 1,
  } as ViewStyle,
});
